<template>
  <div
    class="main-view"
    :key="projectStore.projectId"
    @dragover.prevent
    @drop.prevent="onDropInWindow"
    @dragenter.prevent="onDragEnter"
    @dragleave.prevent="onDragLeave"
  >
    <div class="main-view__preview-area preview-area" @drop.prevent="onDropInCanvas">
      <transition name="header-slide">
        <header-nav
          v-show="isViewLoaded"
          :showForumLink="showControls">
        </header-nav>
      </transition>
      <transition name="main-view__fade-transition">
        <preview-body
          ref="previewBodyRef"
          class="preview-area__preview-body"
          :show-prompt="!showControls"
          :is-dragged="isDragged"
          :load-state="loadState"
          :file-name="loadingFileName"
          @file-selected="onFileSelected"
          v-show="isViewLoaded"
        >
          <template v-slot:canvas>
            <div :class="['preview-area__canvas-wrapper', `preview-area__canvas-wrapper--${orientation}`]">
              <div class="preview-area__canvas-transformer">
                <preview-canvas
                  ref="canvas"
                  class="preview-area__canvas"
                  :show-canvas-controls="showControls"
                  @init-pixi-application="onInitPixiApplication"
                >
                </preview-canvas>
              </div>
            </div>
          </template>
        </preview-body>
      </transition>
      <transition name="main-view__fade-transition">
        <preview-footer
          class="preview-area__preview-footer"
          v-show="isViewLoaded"
          :show-controls="showControls"
          :is-audio-loading="isAudioLoading"
          :track-name="fileName"
          :orientation="orientation"
          @orientation-selected="onOrientationSelected"
          @file-selected="onFileSelected"
          @open-video-export="onOpenVideoExport"
          @toggle-waveform="showOnboardingTooltips = false"
        ></preview-footer>
      </transition>
      <snackbar-container
        class="preview-area__snackbar-container"
        :messages="snackbarMessages"
        @dismiss="dismissMessage"
      ></snackbar-container>
    </div>
    <transition name="main-view__action-layer-slide-transition">
      <action-layer
      v-show="isViewLoaded"
      :class="['main-view__action-layer', { 'main-view__action-layer--disabled': !showControls }]"
      >
      </action-layer>
    </transition>
    <div class="main-view__export-overlay" v-show="exportModalOpen">
    </div>
    <export-modal
      :class="['main-view__export-modal', {'main-view__export-modal--open': exportModalOpen }]"
      v-show="showControls" 
      :is-modal-open="exportModalOpen"
      :canvas-recorder="canvasRecorder"
      @close-video-export="onCloseVideoExport"
    />
    <onboarding-tooltips
      v-if="showControls && showOnboardingTooltips"
    />
  </div>
</template>
<script setup lang="ts">
import { ComponentPublicInstance, computed, onMounted, ref, watch, inject } from 'vue'
import PreviewBody from '@/components/preview/PreviewBody.vue'
import PreviewFooter from '@/components/preview/PreviewFooter.vue'
import ActionLayer from '@/components/ActionLayer.vue'
import SnackbarContainer from '@/components/snackbar/SnackbarContainer.vue'
import useAudioEngine from '@/composables/UseAudioEngine'
import PreviewCanvas from '@/components/preview/PreviewCanvas.vue'
import { Coordinates, Orientation } from '@/types'
import { storeToRefs } from 'pinia'
import { useProjectStore, resolutionSizes } from '@/store/project-store'
import { ExportStatus, useExportStore } from '@/store/export-store'
import { ActionMenuTabs, useApplicationStore } from '@/store/application-store'
import HeaderNav from '@/components/nav/HeaderNav.vue'
import ExportModal from '@/components/export/ExportModal.vue'
import OnboardingTooltips from '@/components/onboarding/OnboardingTooltips.vue'
import * as PIXI from 'pixi.js'
import { CanvasRecorder } from '@/pixi/canvas-recorder'
import { useFileValidator, ValidationError } from '@/composables/UseFileValidator'
import useImageStates from '@/composables/UseImageStates'
import FFmpegUtility from '@/utils/ffmpeg-utility'
import AudioIntensityAnalyzer from '@/audio/AudioIntensityAnalyzer'
import type { SwsClient } from '@serato/sws-sdk'
import { audioExtensionByMimeType } from '@/static/file-types'

const showOnboardingTooltips = ref(true)
const isDragged = ref(false)
const showControls = ref(false)
const loadingFileName = ref('')
const exportModalOpen = ref(false) 
const applicationStore = useApplicationStore()
const { snackbarMessages } = storeToRefs(applicationStore)
const dismissMessage = applicationStore.dismissSnackbarMessage.bind(applicationStore)
const canvasRecorder = ref<CanvasRecorder>()
const canvas = ref<ComponentPublicInstance | null>(null)
const previewBodyHeight = ref(250)
const previewBodyWidth = ref(250)
const previewBodyRef = ref<ComponentPublicInstance | null>(null)

const projectStore = useProjectStore()
const exportStore = useExportStore()
const { orientation } = storeToRefs(projectStore)
const sws: SwsClient | undefined = inject('$swsSdk')

const props = defineProps<{
  isViewLoaded: boolean
}>()

onMounted(() => {
  if (previewBodyRef.value?.$el) {
    new ResizeObserver((entries: ResizeObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.contentRect.height) {
          const newHeight = entry.contentRect.height
          if (newHeight !== previewBodyHeight.value) {
            previewBodyHeight.value = newHeight
          }
        }

        if (entry.contentRect.width) {
          const newWidth = entry.contentRect.width
          if (newWidth !== previewBodyWidth.value) {
            previewBodyWidth.value = newWidth
          }
        }
      })
    }).observe(previewBodyRef.value.$el)
  }
  downloadFileFromCloudLib()
})

const onOpenVideoExport = () => {
  muteAudio()
  exportModalOpen.value = true
  if (!canvasRecorder.value) {
    throw new Error('Cannot export as canvas recorder is not initialised')
  }
  
  if (audioBinary.value) {
    AudioIntensityAnalyzer.resetWeightedIntensity()
    canvasRecorder.value.startRecordCanvas(
      fileName.value, audioBinary.value, untrimmedAudioDurationSeconds.value, loopStartTime.value, loopEndTime.value
    ).catch(e => {
      console.error(e)
      exportStore.exportStatus = ExportStatus.ERROR
      applicationStore.pushSnackbarMessage({
        header: 'Oops something went wrong',
        text: 'Export failed',
        state: 'error'
      })
    })
  } else {
    throw new Error('Cannot export as audioBinary is undefined')
  }
}

const onCloseVideoExport = () => {
  unmuteAudio()
  exportModalOpen.value = false
}

const {
  addAudioFile,
  loadAudioURL,
  playAudio,
  stopAudio,
  loadState,
  muteAudio,
  unmuteAudio,
  fileName,
  audioBinary,
  untrimmedAudioDurationSeconds,
  loopEndTime,
  loopStartTime,
  volumeGain
} = useAudioEngine()

const {
  validateBackgroundFile,
  validateStickerFile
} = useFileValidator()

const { addLogoSticker, addSticker } = useImageStates()

const isAudioLoading = computed(() => {
  return loadState.value === 'loading'
})

const onInitPixiApplication = (application: PIXI.Application) => {
  const ffmpegUtility = FFmpegUtility.getInstance()
  ffmpegUtility.ffmpegLoadedPromise?.catch(error => {
    if (error instanceof RangeError) {
      //Some low end machines unable to load the ffmpeg due to low memeory.
      console.error(error)
      applicationStore.hasEnoughMemory = false
    } else {
      throw error
    }
  })
  canvasRecorder.value = new CanvasRecorder(ffmpegUtility, PIXI.Ticker.shared, application)
}

const onOrientationSelected = (selectedOrientation: Orientation) => {
  orientation.value = selectedOrientation
}

let showControlsTimeout: ReturnType<typeof setTimeout>

watch(loadState, (newLoadState) => {
  if (showControls.value) {
    if (newLoadState === 'loaded') {
      stopAudio()
      playAudio()
      applicationStore.pushSnackbarMessage({
        state: 'success',
        header: 'Audio loaded successfully'
      })
    } else if (newLoadState === 'unloaded') {
      // when the project resets
      showControls.value = false
    }
  } else {
    if (newLoadState === 'loaded') {
      showControlsTimeout = setTimeout(() => {
        showControls.value = true
        playAudio()
      }, 1500)
    } else {
      clearTimeout(showControlsTimeout)
      showControls.value = false
    }
  }
})

const onFileSelected = async (file: File | string, fileName?: string) => {
  applicationStore.clearSnackbarMessages()
  try {
    if (typeof file === 'string') {
      loadingFileName.value = fileName ?? file.substring(file.lastIndexOf('/') + 1)
      await loadAudioURL(file, loadingFileName.value)
    } else {
      loadingFileName.value = fileName ?? file.name
      await addAudioFile(file)
    }
  } catch (e) {
    showSnackBarError(e)
  }
}

const onDragEnter = () => {
  isDragged.value = true
}

const onDragLeave = (dragEvent: DragEvent) => {
  // This guard is required as 'dragleave' events are emitted
  // if the drag goes over a child element. In this case, we don't care
  // about this, only if the drag exits the window
  if (dragEvent.relatedTarget === null) {
    isDragged.value = false
  }
}

// For landing screen audio upload
const onDropInWindow = (drop: DragEvent) => {
  isDragged.value = false
  if (loadState.value !== 'loading' && !showControls.value) {
    const file = drop.dataTransfer?.files.item(0)

    if (drop.dataTransfer?.files.length === 1 && file) {
      onFileSelected(file)
    } else {
      showSnackBarError()
    }
  }
}

const onDropInCanvas = async (drop: DragEvent) => {
  isDragged.value = false
  if (showControls.value && canvas.value && drop.dataTransfer) {
    const file = drop.dataTransfer.files.item(0)
    const dropSrc = drop.dataTransfer.getData('src')

    // From gallery
    if (dropSrc) {
      switch (applicationStore.activeActionTab) {
      case ActionMenuTabs.BACKGROUND: {
        projectStore.setBackground(dropSrc)
        break
      } 
      case ActionMenuTabs.STICKERS: {
        if (applicationStore.isStickerLoading) {
          return
        }
        const coordinates = getImageDropCoordinates(drop)
        addSticker(dropSrc, coordinates)
        break
      }
      }
      return
    }

    // From system
    try {
      if (drop.dataTransfer.files.length !== 1 || !file) {
        throw new ValidationError('Oops something went wrong')
      }

      if (file.type.startsWith('audio/')) {
        onFileSelected(file)
      } else {
        switch (applicationStore.activeActionTab) {
        case ActionMenuTabs.BACKGROUND: {
          await validateBackgroundFile(file)
          projectStore.setBackground(URL.createObjectURL(file), 'fill', file.name)
          break
        } 
        case ActionMenuTabs.STICKERS: {
          validateStickerFile(file)
          const coordinates = getImageDropCoordinates(drop)
          addLogoSticker(URL.createObjectURL(file), file.name, coordinates)
          break
        }
        default: {
          // Display audio errors for other tabs
          onFileSelected(file)
        }
        }
      }
    } catch (e) {
      showSnackBarError(e)
    }
  }
}

const showSnackBarError = (e?: Error | unknown) => {
  if (e instanceof ValidationError) {
    applicationStore.pushSnackbarMessage({
      header: e.title,
      text: e.message,
      state: 'error'
    })
  } else {
    applicationStore.pushSnackbarMessage({
      state: 'error',
      header: 'Oops something went wrong'
    })
  }
}

/**
 * Gets the x,y coordinates to place the sticker after getting dropped onto the canvas
 * If dropped outside the canvas area, the default position is the centre of the canvas
 * @param event the DragEvent onDrop
 * @return sticker coordinates anchored on the centre of the sticker
 */
const getImageDropCoordinates = (event: DragEvent): Coordinates => {
  // Get the first child which is the canvas boundary excluding controls
  const isDroppedInCanvas = canvas.value ? canvas.value.$el.childNodes[0].contains(event.target) : false
  const x = isDroppedInCanvas ? event.offsetX : projectStore.resolution.width / 2
  const y = isDroppedInCanvas ? event.offsetY : projectStore.resolution.height / 2

  return { x, y }
}

const downloadFileFromCloudLib = async () => {
  const queryParams = new URLSearchParams(window.location.search)
  if (queryParams.has('file_id') && sws) {
    const fileId = queryParams.get('file_id') ?? ''
    try {
      // Get file id from the URL
      const response = await sws.cloudlib.getFile({ fileId })
      const downloadFileUrl = response?.download.request.url
      const fileType = audioExtensionByMimeType[response.mime_type]
      if (downloadFileUrl) {
        // Initially set to mute because of the browser's Autoplay policy.
        muteAudio()
        // Set gain 0 to change the volume and slider to 0.
        volumeGain.value = 0.0
        onFileSelected(downloadFileUrl, `studio-track.${fileType}`)
        // URL param/fragment is removed from URL after being read.
        const url = new URL(window.location.href)
        url.searchParams.delete('file_id')
        window.history.replaceState(null, '', url.toString())
      }
    } catch {
      applicationStore.pushSnackbarMessage({
        header: 'Audio file not found',
        text: 'Try exporting again from Studio or manually upload your file.',
        state: 'error'
      })
    }
  }
}

</script>
<style lang="scss">
@use "sass:list";
.main-view {
  position: relative;
  background-color: $grey-90;
  display: flex;
  flex-direction: row;
  height: 100%;

  &__preview-area {
    flex: 1 1;
  }

  &__action-layer {
    flex: 0 0 auto;
    transition: opacity 200ms $animationCurve;

    &--disabled {
      pointer-events: none;
      opacity: 0.2;
    }
  }

  &__export-modal {
    position: absolute;
    bottom: 0px;
    height: 0%;
    width: 100%;
    z-index: 3;
    transition: height 250ms $animationCurve;

    &--open {
      height: calc(100% - 10px);
    }
  }

  &__action-layer-slide-transition {
    &-enter-active,
    &-leave-active {
      transition: all 400ms $animationCurve 400ms;
    }

    &-enter-from,
    &-leave-to {
      transform: translateX(408px);
    }
  }

  &__export-overlay {
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: $black;
    opacity: 75%;
  }

  &__header-slide-transition {
    &-enter-active,
    &-leave-active {
      transition: all 400ms $animationCurve 400ms;
    }

    &-enter-from,
    &-leave-to {
      transform: translateY(-60px);
    }
  }

  &__fade-transition {
    &-enter-active,
    &-leave-active {
      transition: opacity 400ms $animationCurve 800ms;
    }

    &-enter-from,
    &-leave-to {
      opacity: 0;
    }
  }
}

.preview-area {
  display: flex;
  flex-direction: column;
  position: relative;

  &__body {
    flex: 1 1;
  }

  // The wrapper ensures that the sizing of this element in the DOM is the scaled down size - CSS transforms don't
  // affect their flow size so we need a wrapper to manually define how large it is. The contained element will still
  // have the pre-transformed dimensions as far as CSS is concerned.
  &__canvas-wrapper {
    // These properties are to make sure the canvas is centered within the wrapper
    display: flex;
    align-items: center;
    justify-content: center;

    // This mixin provides the canvas scaling factor and pixel width/height, based on the preview body height.
    // Respects the original aspect ratio.
    @mixin responsiveCanvasSizes($height-scale, $min-height, $original) {
      --aspect-ratio: calc(#{list.nth($original, 1)} / #{list.nth($original, 2)});
      --scaled-height: max(calc(#{$height-scale} * v-bind(previewBodyHeight)), #{$min-height});
      --scaled-width: min(calc(var(--aspect-ratio) * var(--scaled-height)), calc(v-bind(previewBodyWidth) - 170)); // Minus 170 to leave room for buttons on right
      --rescaled-height: calc(var(--scaled-width) / var(--aspect-ratio));
      --canvas-scale: #{calc(var(--rescaled-height) / list.nth($original, 2))};
      width: calc(var(--scaled-width) * 1px);
      height: calc(var(--rescaled-height) * 1px);
    }

    &--portrait {
      --portrait-width: v-bind(resolutionSizes.portrait.width);
      --portrait-height: v-bind(resolutionSizes.portrait.height);
      @include responsiveCanvasSizes(
        $height-scale: 0.8,
        $min-height: 476,
        $original: var(--portrait-width) var(--portrait-height)
      )
    }

    &--landscape {
      --landscape-width: v-bind(resolutionSizes.landscape.width);
      --landscape-height: v-bind(resolutionSizes.landscape.height);
      @include responsiveCanvasSizes(
        $height-scale: 0.5,
        $min-height: 310,
        $original: var(--landscape-width) var(--landscape-height)
      )
    }

    &--square {
      --square-width: v-bind(resolutionSizes.square.width);
      --square-height: v-bind(resolutionSizes.square.height);
      @include responsiveCanvasSizes(
        $height-scale: 0.65,
        $min-height: 403,
        $original: var(--square-width) var(--square-height)
      )
    }
  }

  &__canvas-transformer {
    // Scale the canvas using a transform - we want all elements including HTML elements to also be scaled
    transform: scale(var(--canvas-scale), var(--canvas-scale));
  }

  &__footer {
    flex: 0 0 auto;
  }

  &__snackbar-container {
    position: absolute;
    top: 60px;
    right: 0;
  }
}
</style>
